import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  OutlinedInput,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageDropzone from "components/input/ImageDropzone";
import { ServiceToolItem } from "utils/modals";
import { isLocalPreviewUrl } from "utils/commonFunctions";
import { styles } from "modules/serviceManagement/styles";

interface ToolListInputProps {
  label: string;
  placeholder?: string;
  value: ServiceToolItem[];
  error?: string;
  onChange: (next: ServiceToolItem[]) => void;
  onUploadImage: (
    file: File,
    callback: (imageKey: string, imageUrl: string) => void,
  ) => void;
  addButtonLabel?: string;
  hideHeader?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  disabled?: boolean;
}

const ToolListInput: React.FC<ToolListInputProps> = ({
  label,
  placeholder = "Item name",
  value,
  error,
  onChange,
  onUploadImage,
  addButtonLabel = "item",
  hideHeader = false,
  emptyTitle = "No items yet",
  emptyHint = "Upload an image, enter a title, then add it to the list.",
  disabled = false,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<"draft" | number>("draft");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftImage, setDraftImage] = useState("");
  const [draftPreview, setDraftPreview] = useState("");
  const [draftFile, setDraftFile] = useState<File | undefined>();
  const items = Array.isArray(value) ? value : [];
  const canAdd = Boolean(draftTitle.trim());

  const resetDraft = () => {
    setDraftTitle("");
    setDraftImage("");
    setDraftPreview("");
    setDraftFile(undefined);
  };

  const addItem = () => {
    const title = draftTitle.trim();
    if (!title || disabled) return;
    if (items.some((item) => item.title.toLowerCase() === title.toLowerCase())) {
      resetDraft();
      return;
    }
    onChange([
      ...items,
      {
        title,
        image: draftImage,
        previewUrl: draftPreview || undefined,
        file: draftFile,
      },
    ]);
    resetDraft();
  };

  const revokePreview = (url?: string) => {
    if (url && isLocalPreviewUrl(url)) URL.revokeObjectURL(url);
  };

  const removeItem = (index: number) => {
    if (disabled) return;
    const removed = items[index];
    revokePreview(removed?.previewUrl);
    revokePreview(removed?.image);
    onChange(items.filter((_, i) => i !== index));
  };

  const pickImage = (target: "draft" | number) => {
    if (disabled) return;
    uploadTarget.current = target;
    fileRef.current?.click();
  };

  const applyPreview = (
    target: "draft" | number,
    imageKey: string,
    previewUrl: string,
    file?: File,
  ) => {
    if (target === "draft") {
      if (file) setDraftImage(imageKey);
      else if (imageKey) setDraftImage(imageKey);
      setDraftPreview(previewUrl);
      if (file) setDraftFile(file);
      return;
    }
    onChange(
      items.map((item, index) =>
        index === target
          ? {
              ...item,
              image: file ? imageKey : imageKey || item.image,
              previewUrl,
              file: file || item.file,
            }
          : item,
      ),
    );
  };

  const onImagePicked = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file || disabled) return;
    const target = uploadTarget.current;
    if (target === "draft") {
      revokePreview(draftPreview);
    } else {
      revokePreview(items[target]?.previewUrl);
    }
    const localPreview = URL.createObjectURL(file);
    applyPreview(target, "", localPreview, file);
    onUploadImage(file, (imageKey, imageUrl) => {
      const preview =
        imageUrl && imageUrl !== imageKey ? imageUrl : localPreview;
      applyPreview(target, imageKey, preview, file);
    });
    evt.target.value = "";
  };

  return (
    <Box sx={styles.mediaRoot}>
      {!hideHeader && (
        <>
          <Typography sx={styles.mediaLabel}>{label}</Typography>
          <Typography sx={styles.mediaHint} variant="caption">
            Upload an image along with a title.
          </Typography>
        </>
      )}
      {error && (
        <FormHelperText error sx={{ mx: 0, mt: 0 }}>
          {error}
        </FormHelperText>
      )}

      {!disabled ? (
        <Box sx={styles.mediaComposer}>
          <Box
            sx={styles.mediaComposerThumb}
            onClick={() => pickImage("draft")}
          >
            <ImageDropzone
              variant="compact"
              imageUrl={draftPreview}
              title="Image"
              hint="JPG / PNG"
              onClick={() => pickImage("draft")}
            />
          </Box>
          <Box sx={styles.mediaComposerFields}>
            <OutlinedInput
              fullWidth
              value={draftTitle}
              placeholder={placeholder}
              inputProps={{ maxLength: 100, "aria-label": `${label} title` }}
              sx={styles.mediaComposerInput}
              disabled={disabled}
              onChange={(e) => setDraftTitle(e.target.value)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  addItem();
                }
              }}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddRoundedIcon />}
              onClick={addItem}
              disabled={!canAdd || disabled}
              sx={styles.mediaComposerButton}
            >
              Add {addButtonLabel}
            </Button>
          </Box>
        </Box>
      ) : null}

      {items.length === 0 ? (
        <Box sx={styles.emptyState}>
          <Typography sx={styles.emptyStateTitle}>{emptyTitle}</Typography>
          <Typography sx={styles.emptyStateHint}>{emptyHint}</Typography>
        </Box>
      ) : (
        <Box sx={styles.mediaList}>
          {items.map((item, index) => (
            <Box key={item._id || `${item.title}-${index}`} sx={styles.mediaItem}>
              <Box
                sx={styles.mediaItemThumb}
                onClick={() => pickImage(index)}
              >
                <ImageDropzone
                  variant="inline"
                  imageUrl={item.previewUrl || item.image}
                  title="Image"
                  hint=""
                  onClick={() => pickImage(index)}
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={styles.mediaItemTitle}>{item.title}</Typography>
                <Typography sx={styles.mediaItemMeta}>
                  {item.previewUrl || item.image ? "Image attached" : "No image"}
                </Typography>
              </Box>
              {!disabled ? (
                <IconButton
                  size="small"
                  sx={styles.mediaItemDelete}
                  onClick={() => removeItem(index)}
                  disabled={disabled}
                  aria-label={`Remove ${item.title}`}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          ))}
        </Box>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={onImagePicked}
      />
    </Box>
  );
};

export default ToolListInput;
